// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import XngSticky from "@conklin/App/Components/XngSticky";
import StyledBox, { StyledBoxStyles } from "@conklin/XDL/Components/Common/Box";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    imageSection?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    additionalContent?: InjectableCss;
    actions?: InjectableCss;
    bottomSection?: InjectableCss;
    separator?: InjectableCss;
    divider?: InjectableCss;
    descriptionAndDocuments?: InjectableCss;
    leftTopPart?: InjectableCss;
    actionsBox?: StyledBoxStyles;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = props => {
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.leftSection} data-test-selector="productDetails_leftColumn">
                <Hidden above="md">
                    <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Details Title" />
                    <StyledWrapper {...styles.productNumbersWrapper}>
                        {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                            fields: {},
                        })}
                        {createWidgetElement("ProductDetails/ProductDetailsSkuNumber", {
                            fields: {},
                        })}
                    </StyledWrapper>
                </Hidden>
                <StyledWrapper {...styles.leftTopPart}>
                    <StyledWrapper {...styles.imageSection}>
                        <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image & Image Carousel" />
                    </StyledWrapper>
                    <Hidden below="lg">
                        <StyledWrapper>
                            <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Details Title" />
                            <StyledWrapper {...styles.productNumbersWrapper}>
                                {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                                    fields: {},
                                })}
                                {createWidgetElement("ProductDetails/ProductDetailsSkuNumber", {
                                    fields: {},
                                })}
                                <StyledWrapper {...styles.divider} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.descriptionAndDocuments} data-test-selector="description-and-documents">
                                <XngZone
                                    contentId={props.id}
                                    zoneName="Content3"
                                    hint="Zone For Product Description & Documents"
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    </Hidden>
                </StyledWrapper>
                <StyledWrapper {...styles.separator} />
                <Hidden below="lg">
                    <StyledWrapper {...styles.additionalContent}>
                        <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Products Carousel" />
                        <XngZone
                            contentId={props.id}
                            zoneName="Content6"
                            hint="Zone For Product Description and specifications"
                        />
                    </StyledWrapper>
                </Hidden>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection} data-test-selector="productDetails_rightColumn">
                <XngSticky>
                    <StyledBox extendedStyles={styles.actionsBox}>
                        <XngZone contentId={props.id} zoneName="Content7" hint="Zone For ProductDetailsActions" />
                    </StyledBox>
                    <StyledWrapper {...styles.actions}>
                        <XngZone contentId={props.id} zoneName="Content8" hint="Zone For Product Share" />
                    </StyledWrapper>
                    <Hidden below="md">
                        <XngZone contentId={props.id} zoneName="Content9" hint="Zone For Additional Content" />
                    </Hidden>
                </XngSticky>
            </StyledWrapper>
            <StyledWrapper {...styles.bottomSection}>
                <Hidden above="md">
                    <StyledWrapper {...styles.descriptionAndDocuments}>
                        <XngZone
                            contentId={props.id}
                            zoneName="Content3"
                            hint="Zone For Product Description & Documents"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.additionalContent}>
                        <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Products Carousel" />
                        <XngZone
                            contentId={props.id}
                            zoneName="Content6"
                            hint="Zone For Product Description and specifications"
                        />
                    </StyledWrapper>
                </Hidden>
                <Hidden above="sm">
                    <XngZone contentId={props.id} zoneName="Content9" hint="Zone For Additional Content" />
                </Hidden>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
